/**
 * Cinematic atmosphere layer — an "imperial palace" ambience rendered on a
 * single canvas behind the app: floating dust, golden embers, drifting blossom
 * petals, slow fog, plus optional rain/snow. Cursor movement drives a parallax
 * light and a subtle scene tilt (via CSS custom properties the theme reads).
 *
 * Everything is procedural — no external assets — and it degrades gracefully:
 * fewer particles on small screens, paused when the tab is hidden, and disabled
 * entirely under `prefers-reduced-motion`.
 */

export type Weather = "clear" | "rain" | "snow";
export type TimeOfDay = "day" | "night";

const KEY_WEATHER = "xq-weather";
const KEY_TIME = "xq-time";

let weather: Weather = (localStorage.getItem(KEY_WEATHER) as Weather) || "clear";
let timeOfDay: TimeOfDay = (localStorage.getItem(KEY_TIME) as TimeOfDay) || "day";

export const getWeather = () => weather;
export const getTimeOfDay = () => timeOfDay;
export function setWeather(w: Weather) {
  weather = w;
  localStorage.setItem(KEY_WEATHER, w);
  applyTheme();
}
export function setTimeOfDay(t: TimeOfDay) {
  timeOfDay = t;
  localStorage.setItem(KEY_TIME, t);
  applyTheme();
}
function applyTheme() {
  document.documentElement.dataset.time = timeOfDay;
  document.documentElement.dataset.weather = weather;
}

interface P { x: number; y: number; vx: number; vy: number; r: number; a: number; kind: number; rot: number; vr: number; }

const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
let ctx: CanvasRenderingContext2D | null = null;
let layer: HTMLCanvasElement | null = null;
let W = 0, H = 0, dpr = 1;
let dust: P[] = [];
let petals: P[] = [];
let embers: P[] = [];
let drops: P[] = [];
let raf = 0;
let running = false;

function rand(a: number, b: number) { return a + Math.random() * (b - a); }

/**
 * Backing-store ceiling. Firefox on Android refuses anything over its canvas
 * size limit and throws from setTransform, after which every draw throws too
 * ("already in error state") — seven reports from one visitor on a puzzle page.
 * A layer of dust and petals never needs more than this, so scale down to fit.
 */
const MAX_SIDE = 4096;
const MAX_PIXELS = 8_000_000;

function resize(canvas: HTMLCanvasElement) {
  W = canvas.clientWidth;
  H = canvas.clientHeight;
  dpr = Math.min(2, window.devicePixelRatio || 1);
  if (W > 0 && H > 0) dpr = Math.min(dpr, MAX_SIDE / W, MAX_SIDE / H, Math.sqrt(MAX_PIXELS / (W * H)));
  canvas.width = Math.max(1, Math.floor(W * dpr));
  canvas.height = Math.max(1, Math.floor(H * dpr));
  ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  seed();
}

/** The layer is decoration: if the browser will not draw it, take it away quietly. */
function disable(canvas: HTMLCanvasElement) {
  running = false;
  cancelAnimationFrame(raf);
  ctx = null;
  canvas.remove();
}

function count(base: number) {
  const area = (W * H) / (1280 * 720);
  return Math.max(6, Math.round(base * Math.min(1.4, area)));
}

function seed() {
  const dustN = count(46);
  const petalN = count(14);
  const emberN = count(10);
  dust = Array.from({ length: dustN }, () => ({ x: rand(0, W), y: rand(0, H), vx: rand(-6, 6), vy: rand(-10, -3), r: rand(0.6, 2.1), a: rand(0.05, 0.35), kind: 0, rot: 0, vr: 0 }));
  petals = Array.from({ length: petalN }, () => ({ x: rand(0, W), y: rand(-H, H), vx: rand(-14, -4), vy: rand(10, 26), r: rand(5, 10), a: rand(0.25, 0.7), kind: 0, rot: rand(0, 6.28), vr: rand(-1.2, 1.2) }));
  embers = Array.from({ length: emberN }, () => ({ x: rand(0, W), y: rand(0, H), vx: rand(-4, 4), vy: rand(-16, -6), r: rand(0.8, 2.0), a: rand(0.2, 0.6), kind: 0, rot: 0, vr: 0 }));
  drops = [];
}

function step(dt: number) {
  if (!ctx) return;
  ctx.clearRect(0, 0, W, H);

  // Dust — slow rising motes catching the light.
  ctx.fillStyle = "#f3e2c0";
  for (const p of dust) {
    p.x += p.vx * dt; p.y += p.vy * dt;
    if (p.y < -10) { p.y = H + 10; p.x = rand(0, W); }
    if (p.x < -10) p.x = W + 10; else if (p.x > W + 10) p.x = -10;
    ctx.globalAlpha = p.a;
    ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.283); ctx.fill();
  }

  // Golden embers with a soft glow.
  for (const p of embers) {
    p.x += p.vx * dt + Math.sin(p.y * 0.02) * 4 * dt; p.y += p.vy * dt;
    if (p.y < -10) { p.y = H + 10; p.x = rand(0, W); }
    ctx.globalAlpha = p.a;
    const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 6);
    g.addColorStop(0, "rgba(255,210,120,0.9)");
    g.addColorStop(1, "rgba(255,170,60,0)");
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 6, 0, 6.283); ctx.fill();
  }

  // Cherry-blossom petals (clear weather only).
  if (weather === "clear") {
    for (const p of petals) {
      p.x += p.vx * dt; p.y += p.vy * dt; p.rot += p.vr * dt;
      if (p.y > H + 20) { p.y = -20; p.x = rand(0, W); }
      if (p.x < -20) p.x = W + 20;
      ctx.save();
      ctx.translate(p.x, p.y); ctx.rotate(p.rot);
      ctx.globalAlpha = p.a;
      ctx.fillStyle = "#e7b6c4";
      ctx.beginPath();
      ctx.ellipse(0, 0, p.r, p.r * 0.55, 0, 0, 6.283);
      ctx.fill();
      ctx.restore();
    }
  }

  // Rain / snow.
  if (weather !== "clear") {
    if (drops.length < count(weather === "rain" ? 220 : 120)) {
      for (let i = 0; i < 6; i++) drops.push({ x: rand(0, W), y: rand(-H, 0), vx: weather === "rain" ? -60 : rand(-14, 6), vy: weather === "rain" ? rand(700, 950) : rand(50, 90), r: weather === "rain" ? rand(6, 12) : rand(1.4, 3), a: weather === "rain" ? 0.28 : 0.85, kind: 0, rot: 0, vr: 0 });
    }
    if (weather === "rain") { ctx.strokeStyle = "rgba(200,220,235,0.35)"; ctx.lineWidth = 1; }
    else ctx.fillStyle = "#f4f6ff";
    for (const p of drops) {
      p.x += p.vx * dt; p.y += p.vy * dt;
      if (p.y > H + 20) { p.y = -20; p.x = rand(0, W); }
      ctx.globalAlpha = p.a;
      if (weather === "rain") { ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + p.vx * 0.02, p.y + p.r); ctx.stroke(); }
      else { ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.283); ctx.fill(); }
    }
  } else if (drops.length) drops = [];

  ctx.globalAlpha = 1;
}

let last = 0;
function loop(now: number) {
  if (!running) return;
  const dt = Math.min(0.05, (now - last) / 1000 || 0.016);
  last = now;
  try {
    step(dt);
  } catch {
    if (layer) disable(layer);
    return;
  }
  raf = requestAnimationFrame(loop);
}

function setupParallax() {
  const root = document.documentElement;
  const set = (mx: number, my: number) => {
    root.style.setProperty("--mx", mx.toFixed(3));
    root.style.setProperty("--my", my.toFixed(3));
  };
  window.addEventListener("pointermove", (e) => {
    set((e.clientX / window.innerWidth - 0.5) * 2, (e.clientY / window.innerHeight - 0.5) * 2);
  }, { passive: true });
  window.addEventListener("deviceorientation", (e) => {
    if (e.gamma == null || e.beta == null) return;
    set(Math.max(-1, Math.min(1, e.gamma / 30)), Math.max(-1, Math.min(1, (e.beta - 45) / 30)));
  });
}

export function initCinematic() {
  applyTheme();
  setupParallax();
  if (reduced) return; // respect the user's motion preference

  const canvas = document.createElement("canvas");
  canvas.className = "atmos-canvas";
  document.body.prepend(canvas);
  ctx = canvas.getContext("2d");
  if (!ctx) return;
  layer = canvas;
  const onResize = () => {
    if (!ctx) return;
    try { resize(canvas); } catch { disable(canvas); }
  };
  onResize();
  if (!ctx) return;
  window.addEventListener("resize", onResize);
  running = true;
  raf = requestAnimationFrame(loop);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) { running = false; cancelAnimationFrame(raf); }
    else if (!running && ctx) { running = true; last = performance.now(); raf = requestAnimationFrame(loop); }
  });
}

/** Fade out and remove the loading overlay once the app has painted. */
export function dismissLoader() {
  const el = document.getElementById("loader");
  if (!el) return;
  el.classList.add("gone");
  setTimeout(() => el.remove(), 900);
}

/** A one-shot golden burst from the board centre for a victory. */
export function victoryBurst() {
  if (reduced) return;
  const layer = document.createElement("div");
  layer.className = "fx-burst";
  const n = 34;
  for (let i = 0; i < n; i++) {
    const s = document.createElement("i");
    const ang = (i / n) * 6.283 + Math.random();
    const dist = 120 + Math.random() * 220;
    s.style.setProperty("--dx", `${Math.cos(ang) * dist}px`);
    s.style.setProperty("--dy", `${Math.sin(ang) * dist}px`);
    s.style.setProperty("--d", `${Math.random() * 0.25}s`);
    s.textContent = ["✦", "❖", "帥", "❉"][i % 4]!;
    layer.append(s);
  }
  document.body.append(layer);
  setTimeout(() => layer.remove(), 2200);
}
